import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isBuyable } from "../../site/pages/Products";
import { FwTerms } from "./LaonaFirmware";
import { manualApi, projectApi, won } from "./LaonaProjects";

/* =========================================================
   프로젝트 부품 주문 — 내 프로젝트(수동) 목록의 [주문하기] 팝업.
   서버(app/laona_manual.py /parts)가 연결도에 놓인 모듈 · 연결 리스트의 모듈 · 브레드보드를 세어 주고,
   가격 · 판매 상태는 공개 제품 목록(/api/products)에서 상품코드로 확인한다.
   수량을 고칠 수 있고, 판매 중이며 가격이 있는 부품만 I.META 장바구니(/cart?add=CODE:수량,…)에 담긴다.
   프로젝트 번호(&project=ID)도 함께 넘겨, 주문서를 내면 주문이 프로젝트에 이어지고 자료 다운로드가 열린다.
   펌웨어 개발의뢰(app/laona_fw.py)를 해 두었으면 [펌웨어 빌드 포함]을 고를 수 있다 — 체크하면 &fw=의뢰ID 로
   기본 빌드비가 장바구니에 함께 담겨 부품과 한 주문으로 낸다.
   ========================================================= */

function shopState(code, byCode) {
  if (!code) return { k: "none", t: "판매 준비 중", p: null };
  const p = byCode.get(code.toUpperCase());
  if (!p) return { k: "missing", t: "쇼핑몰 미등록", p: null };
  if (p.status === "SOLDOUT") return { k: "soldout", t: "품절", p };
  if (!isBuyable(p)) return { k: "inquiry", t: "가격 문의", p };
  return { k: "ok", t: "담기 가능", p };
}

export default function OrderDialog({ project, onClose }) {
  const ref = useRef(null);
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", rows: [], error: "" });
  /* 라오나봇 프로젝트(kind "bot") — 부품은 라오나봇 설계에서, 펌웨어 개발 · 빌드는 체크된 채 고정 */
  const bot = project.kind === "bot";
  const fwReq = project.fw && project.fw.status === "REQUESTED" ? project.fw : null; // 아직 주문하지 않은 개발의뢰
  const [fwOn, setFwOn] = useState(!!fwReq);
  const fwSel = bot ? !!fwReq : fwOn;
  const [fee, setFee] = useState(null);

  useEffect(() => {
    if (!fwReq) return undefined;
    let alive = true;
    fetch("/api/laona/fw/fee", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && d && setFee(d.fee))
      .catch(() => {});
    return () => { alive = false; };
  }, [fwReq]);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([
      bot ? projectApi(`/${project.id}/parts`) : manualApi(`/${project.id}/parts`),
      fetch("/api/products").then((r) => (r.ok ? r.json() : { items: [] })).catch(() => ({ items: [] })),
    ])
      .then(([parts, products]) => {
        const byCode = new Map((products.items || []).filter((p) => p.code).map((p) => [p.code.trim().toUpperCase(), p]));
        const rows = parts.items.map((it) => ({ ...it, st: shopState(it.code, byCode) }));
        if (alive) setState({ status: "ready", rows, error: "" });
      })
      .catch((err) => alive && setState({ status: "error", rows: [], error: err.message }));
    return () => { alive = false; };
  }, [project.id, bot]);

  const setQty = (i, v) => setState((s) => ({
    ...s,
    rows: s.rows.map((r, j) => (j === i ? { ...r, qty: Math.max(0, Math.min(99, parseInt(v, 10) || 0)) } : r)),
  }));

  /* 같은 상품코드를 쓰는 부품은 장바구니에서 한 줄로 합친다 */
  const cart = new Map();
  for (const r of state.rows) {
    if (r.st.k !== "ok" || r.qty <= 0) continue;
    const code = r.code.toUpperCase();
    const o = cart.get(code);
    cart.set(code, { p: r.st.p, n: (o ? o.n : 0) + r.qty });
  }
  let sum = 0;
  let cnt = 0;
  for (const v of cart.values()) { sum += v.p.price * v.n; cnt += v.n; }

  function go() {
    const list = [...cart.entries()].map(([c, v]) => `${encodeURIComponent(c)}:${Math.min(99, v.n)}`).join(",");
    const fw = fwReq && fwSel ? `&fw=${fwReq.id}` : "";
    navigate(`/cart?add=${encodeURIComponent(list)}&from=${bot ? "bot" : "project"}&project=${project.id}${fw}`);
  }

  return (
    <dialog
      ref={ref}
      className="lb-dialog lb-dialog--order"
      aria-labelledby="lb-order-title"
      onClose={onClose}
      onClick={(e) => { if (e.target === ref.current) ref.current.close(); }}
    >
      <div className="lb-dialog__box">
        <div className="lb-dialog__head">
          <h2 id="lb-order-title">부품 주문하기</h2>
          <button type="button" className="lb-dialog__x" aria-label="닫기" onClick={() => ref.current.close()}>×</button>
        </div>
        <p className="lb-dialog__lead">
          <b>{project.title}</b> — {bot ? "라오나봇 설계의 모듈" : "연결도에 놓인 모듈"}과 브레드보드를 모았습니다. 수량을 고칠 수 있고, ‘담기 가능’인 부품만 장바구니에 담깁니다.
          {bot ? " 라오나봇 프로젝트는 펌웨어 개발 · 빌드가 함께 주문됩니다."
            : " 장바구니에서 주문을 마치면 이 프로젝트의 자료 다운로드(결선도 · 배선도 · 전체 BOM)가 열립니다."}
        </p>

        {state.status === "loading" && <p className="lb-empty">부품 목록을 불러오는 중입니다…</p>}
        {state.status === "error" && <p className="lb-error">{state.error || "부품 목록을 불러오지 못했습니다."}</p>}
        {state.status === "ready" && !state.rows.length && (
          <p className="lb-empty">연결도에 놓인 모듈이 없습니다. 프로젝트를 열어 모듈을 배치해 주세요.</p>
        )}
        {state.status === "ready" && state.rows.length > 0 && (
          <>
            <div className="lb-order__wrap">
              <table className="lb-order">
                <thead>
                  <tr><th>부품</th><th>수량</th><th>쇼핑몰 상품</th><th className="num">단가</th><th className="num">금액</th><th>상태</th></tr>
                </thead>
                <tbody>
                  {state.rows.map((r, i) => (
                    <tr key={`${r.kind}:${r.id}`} className={`st-${r.st.k}`}>
                      <td>
                        <span className="lb-order__mod">
                          {r.image ? <img alt="" src={r.image} /> : <span className="lb-order__noimg" aria-hidden="true" />}
                          <span><b>{r.id}</b>{r.kind === "breadboard" && <small>브레드보드</small>}</span>
                        </span>
                      </td>
                      <td>
                        <input className="lb-order__qty" type="number" min="0" max="99" value={r.qty}
                               aria-label={`${r.id} 수량`} onChange={(e) => setQty(i, e.target.value)} />
                      </td>
                      <td className="lb-order__pname">{r.st.p ? r.st.p.name : "—"}</td>
                      <td className="num">{r.st.p && r.st.p.price != null ? won(r.st.p.price) : "—"}</td>
                      <td className="num">{r.st.k === "ok" ? won(r.st.p.price * r.qty) : "—"}</td>
                      <td><span className={`lb-order__st lb-order__st--${r.st.k}`}>{r.st.t}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="lb-order__sum">
              <span>담을 부품 <b>{cart.size}종 {cnt}개</b></span>
              <span>상품 금액 <b>{won(sum)}</b></span>
              <span className="lb-order__note">배송비는 장바구니에서 계산됩니다.</span>
            </div>
            {state.rows.some((r) => r.st.k !== "ok") && (
              <p className="lb-order__note">‘판매 준비 중’ · ‘쇼핑몰 미등록’ · ‘품절’ · ‘가격 문의’ 부품은 담기지 않습니다.</p>
            )}
          </>
        )}

        {fwReq && (
          <div className="lb-fw__order">
            <label className="lb-check">
              <input type="checkbox" checked={fwSel} disabled={bot} onChange={(e) => setFwOn(e.target.checked)} />
              <span>
                <b>펌웨어 빌드 포함</b> (+{fee == null ? "…" : won(fee)}) — 펌웨어 개발의뢰 {fwReq.request_no}
              </span>
            </label>
            {bot && <span className="lb-order__note">라오나봇 프로젝트는 펌웨어 개발 · 빌드를 빼고 주문할 수 없습니다.</span>}
            {fwSel && fee != null && <FwTerms fee={fee} />}
          </div>
        )}
        {project.fw && project.fw.status === "ORDERED" && (
          <p className="lb-order__note">펌웨어 개발의뢰 {project.fw.request_no}는 이미 주문 #{project.fw.order_no}에 포함되어 있습니다.</p>
        )}

        <div className="lb-order__acts">
          <button type="button" className="lb-btn" onClick={() => ref.current.close()}>닫기</button>
          <button type="button" className="lb-btn lb-btn--primary" disabled={!cart.size && !(fwReq && fwSel)} onClick={go}>
            장바구니에 담기{cart.size ? ` (${cart.size}종 ${cnt}개${fwReq && fwSel ? " + 펌웨어 빌드" : ""})` : fwReq && fwSel ? " (펌웨어 빌드)" : ""}
          </button>
        </div>
      </div>
    </dialog>
  );
}
